<template>
  <div>
    <Modal
      v-if="keyToShow"
      :value="keyToShow !== null"
      @hide="keyToShow = null"
      :title="`API example (${keyToShow.name})`"
    >
      <template slot="body" class="content">
        <p>
          Here is an example of how to get all domains in PowerShell and parsing
          the result into PS objects.
        </p>

        <pre
          class="is-primary is-size-7"
        >$data = (Invoke-WebRequest -uri <b class="has-text-info">{{
            getUrl(keyToShow)
          }}/secure/domains</b> -headers @{ '<b class="has-text-primary">x-api-key</b>'='<b class="has-text-primary">{{
            keyToShow.value
          }}</b>';}).content | convertfrom-json</pre>

        <p class="mt-5">
          Print the result with:
        </p>
        <pre>echo $data</pre>

        <p class="mt-5">
          We can also iterate through these objects and print only domain UIDs
          if we want to.
        </p>

        <pre class="is-primary is-size-7">$data | foreach{$_.uid}</pre>
      </template>
    </Modal>

    <Confirm
      v-if="keyToDelete !== null"
      :value="!!keyToDelete"
      @hide="keyToDelete = null"
      @confirmed="deleteKey(keyToDelete)"
    >
      You are about to delete an API key ({{ keyToDelete.name }}). No requests
      with this key can be made when it's deleted.
    </Confirm>

    <div class="table-container">
      <div v-if="isLoading" class="center">
        <span class="spinner"></span>
      </div>

      <i v-else-if="!apiKeys || apiKeys.length === 0">No API keys.</i>

      <table v-else class="table is-fullwidth is-narrow">
        <thead>
          <tr class="is-unselectable">
            <th>Name</th>
            <th>Key</th>
            <th>Created at</th>
            <th>Last usage</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="apiKey of apiKeys" :key="apiKey.uid">
            <td class="is-clickable">
              <span
                @click.stop="keyToEdit = apiKey"
                v-if="!keyToEdit || apiKey.uid !== keyToEdit.uid"
                >{{ apiKey.name }}</span
              >
              <EditSingleField
                v-else
                :text="keyToEdit.name"
                validation="required"
                type="text"
                name="name"
                @save="saveKeyName(keyToEdit, $event)"
                @cancel="keyToEdit = null"
              ></EditSingleField>
            </td>

            <td>
              <button
                class="button is-small smaller is-info mr-2"
                @click="copyToClipboard(apiKey.uid)"
              >
                Copy to clipboard
              </button>

              <button
                class="button is-small smaller"
                @click="keyToShow = apiKey"
              >
                Show example
              </button>

              <span style="font-size: 0px" :id="apiKey.uid">{{
                apiKey.value
              }}</span>
            </td>

            <td>{{ getDate(apiKey.createdAt) }}</td>

            <td>
              <span v-if="!apiKey.accessedAt"><i>Never been used</i></span>
              <span v-else>
                {{ getDate(apiKey.accessedAt) }}
              </span>
            </td>

            <td>
              <button
                class="button is-small smaller is-danger"
                @click="keyToDelete = apiKey"
              >
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button class="button is-primary is-small" @click="addKey()">
      Add key
    </button>
  </div>
</template>

<script>
import { postApi } from "@/utils/http";
import { toReadable } from "@/utils/date";
import { copyToClipboard } from "@/utils/document";

export default {
  components: {
    Modal: () => import("@/components/main/Modal.vue"),
    Confirm: () => import("@/components/main/Confirm.vue"),
    EditSingleField: () =>
      import("@/components/singles/EditSingleField/component.vue"),
  },
  data() {
    return {
      apiKeys: [],
      isLoading: false,
      keyToShow: null,
      keyToDelete: null,
      keyToEdit: null,
    };
  },

  created() {
    this.getApiKeys();
  },

  methods: {
    getUrl() {
      return `${location.protocol}//${this.$store.state.settings.server.baseApi}`;
    },
    async saveKeyName(apiKey, newName) {
      await postApi("set/userApiKeyName", {
        uid: apiKey.uid,
        name: newName,
      });

      apiKey.name = newName;

      this.keyToEdit = null;
    },
    async addKey() {
      this.isLoading = true;

      await postApi("create/userApiKey");
      await this.getApiKeys();

      this.isLoading = false;
    },
    copyToClipboard(elementId) {
      copyToClipboard(elementId);
      this.$helpers.notify(
        "Copied",
        "Copied API key to clipboard.",
        "info",
        6000
      );
    },
    getDate(date) {
      return toReadable(date);
    },
    async getApiKeys() {
      try {
        this.isLoading = true;

        const data = await postApi("get/userApiKeys", {});
        this.apiKeys = data;

        this.isLoading = false;
      } catch (e) {
        this.$helpers.notify("Error", "Could not get API keys.", "danger");
      }
    },

    async deleteKey(apiKey) {
      await postApi("delete/userApiKey", { uid: apiKey.uid });
      await this.getApiKeys();

      this.keyToDelete = null;
    },
  },
};
</script>

<style scoped>
.center {
  display: flex;
  justify-content: center;
}
</style>
